import { useCallback, useMemo, useRef } from "react";
import type { BoardCollapsedChange } from "@design-system/Board/Board.types";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import {
    type BoardColumnFill,
    getBoardColumnFills,
    getToggledColumnIds,
} from "@components/views/board/boardColumnCollapse";
import type { BoardCollapsed } from "@components/views/board/boardLayoutCache";
import {
    NO_VIEW_KEY,
    type UpdateStoredBoardCollapse,
    useBoardColumnCollapse,
} from "@components/views/board/useBoardColumnCollapse";
import {
    clearDroppedOverrides,
    EMPTY_VIEW_COLLAPSE,
    getHandCollapsedIds,
    patchViewCollapse,
    restoreClearedOverrides,
    type TaskBoardCollapseByView,
    toHandCollapsedOverrides,
    type UpdateTaskBoardCollapse,
    updateViewColumnOverrides,
} from "./taskBoardCollapse";
import type { TaskBoardModel } from "./useTaskBoardModel";

const NO_COLUMN_FILLS: BoardColumnFill[] = [];

interface UseTaskBoardCollapseArgs {
    /** The picked view; null with none picked. */
    viewId: string | null;
    collapseByViewId: TaskBoardCollapseByView;
    onCollapseByViewIdChange: UpdateTaskBoardCollapse;
    /** The columns and their committed tasks; null while the board isn't showing them. */
    board: Pick<TaskBoardModel, "columns" | "tasksByColumnId"> | null;
}

/** Puts back the overrides a drop cleared in these columns, once its cards went back. */
export type RestoreDroppedColumns = (columnIds: readonly string[]) => void;

/** What the board collapses, and the handlers that keep it per view. */
export interface TaskBoardCollapseState extends BoardCollapsed {
    onCollapsedChange: (change: BoardCollapsedChange) => void;
    /**
     * Cards a drop landed in these columns: it expands a column collapsed by
     * hand, on every view, and returns how to undo that if the cards go back.
     */
    onCardsDropped: (columnIds: readonly string[]) => RestoreDroppedColumns;
}

const NO_RESTORE: RestoreDroppedColumns = () => {};

/** A view's column, as the key of its toggle count. */
const getToggleKey = (viewKey: string, columnId: string) =>
    `${viewKey}/${columnId}`;

/**
 * The view's collapsed columns and lanes. Empty columns collapse, live, on
 * every view; emptiness comes from the committed tasks, so a drag's source
 * column collapses once the drop lands. Expanding an empty column holds for
 * the session only, whatever its cards do next. Toggling a column with cards
 * is stored, and a hand collapse holds until toggled back or a card is
 * dropped in; the stored state never changes as the tasks load or change.
 */
export const useTaskBoardCollapse = ({
    viewId,
    collapseByViewId,
    onCollapseByViewIdChange,
    board,
}: UseTaskBoardCollapseArgs): TaskBoardCollapseState => {
    const viewKey = viewId ?? NO_VIEW_KEY;
    const { columnOverrides: overrides, collapsedLaneIds } =
        collapseByViewId[viewKey] ?? EMPTY_VIEW_COLLAPSE;
    const handCollapsedIds = useMemo(
        () => getHandCollapsedIds(overrides),
        [overrides],
    );
    const columns = useMemo(
        () =>
            board === null
                ? NO_COLUMN_FILLS
                : getBoardColumnFills({
                      columns: board.columns,
                      itemsByColumnId: board.tasksByColumnId,
                  }),
        [board],
    );
    const latestChangeRef = useLatestRef(onCollapseByViewIdChange);
    const toggleCountsRef = useRef(new Map<string, number>());
    /** How often the user toggled this view's column, so a drop's undo skips one toggled since. */
    const countToggles = useCallback(
        (columnId: string) =>
            toggleCountsRef.current.get(getToggleKey(viewKey, columnId)) ?? 0,
        [viewKey],
    );

    const updateStored = useCallback<UpdateStoredBoardCollapse>(
        (next) =>
            onCollapseByViewIdChange((latest) => {
                const latestView = latest[viewKey] ?? EMPTY_VIEW_COLLAPSE;
                const stored = next({
                    collapsedColumnIds: getHandCollapsedIds(
                        latestView.columnOverrides,
                    ),
                    collapsedLaneIds: latestView.collapsedLaneIds,
                });
                return stored === null
                    ? latest
                    : patchViewCollapse({
                          collapseByViewId: latest,
                          viewKey,
                          patch: {
                              columnOverrides: toHandCollapsedOverrides(
                                  stored.collapsedColumnIds,
                              ),
                              collapsedLaneIds: stored.collapsedLaneIds,
                          },
                      });
            }),
        [onCollapseByViewIdChange, viewKey],
    );
    const { collapsedColumnIds, onCollapsedChange: onColumnCollapseChange } =
        useBoardColumnCollapse({
            viewKey,
            columns,
            handCollapsedIds,
            updateStored,
        });

    const onCollapsedChange = useCallback(
        (change: BoardCollapsedChange) => {
            for (const columnId of getToggledColumnIds({
                collapsedColumnIds,
                nextCollapsedColumnIds: change.collapsedColumnIds,
            })) {
                toggleCountsRef.current.set(
                    getToggleKey(viewKey, columnId),
                    countToggles(columnId) + 1,
                );
            }
            onColumnCollapseChange(change);
        },
        [collapsedColumnIds, countToggles, viewKey, onColumnCollapseChange],
    );

    const onCardsDropped = useCallback(
        (columnIds: readonly string[]): RestoreDroppedColumns => {
            const next = clearDroppedOverrides({ overrides, columnIds });
            if (next === overrides) {
                return NO_RESTORE;
            }
            onCollapseByViewIdChange(
                updateViewColumnOverrides({
                    viewKey,
                    update: (latestOverrides) =>
                        clearDroppedOverrides({
                            overrides: latestOverrides,
                            columnIds,
                        }),
                }),
            );
            const clearedOverrides = overrides.filter(
                (override) => !next.includes(override),
            );
            const togglesAtDrop = new Map(
                clearedOverrides.map(({ columnId }) => [
                    columnId,
                    countToggles(columnId),
                ]),
            );
            return (revertedColumnIds) => {
                const untoggledColumnIds = revertedColumnIds.filter(
                    (columnId) =>
                        countToggles(columnId) === togglesAtDrop.get(columnId),
                );
                latestChangeRef.current(
                    updateViewColumnOverrides({
                        viewKey,
                        update: (latestOverrides) =>
                            restoreClearedOverrides({
                                overrides: latestOverrides,
                                clearedOverrides,
                                columnIds: untoggledColumnIds,
                            }),
                    }),
                );
            };
        },
        [
            overrides,
            viewKey,
            countToggles,
            onCollapseByViewIdChange,
            latestChangeRef,
        ],
    );

    return {
        collapsedColumnIds,
        collapsedLaneIds,
        onCollapsedChange,
        onCardsDropped,
    };
};
